import { useEffect, FC } from 'react';
import { useForm } from 'react-hook-form';

import {
  initCustomerFormFields,
  type CustomerFormFields,
} from 'page/CustomerPage/types/customerFormField';
import { useEditStore, useListStore } from 'page/CustomerPage/hooks';
import { InputModal } from './InputModal';
import { ConfirmationModal } from './ConfirmationModal';
import { DeleteModal } from './DeleteModal';
import { useContractListStore } from '../../hooks/useContractListStore';

export const EditModal: FC = () => {
  const {
    name,
    detail,
    environment,
    environmentType,
    corporateNumber,
    channel,
    poseidonUrl,
    status,
    systemUpdateGroup,
    iapClientId,
    iapClientSecret,
    currentSystemVersion,
    targetSystemVersion,
    poseidonAgentCpu,
    poseidonAgentMemory,
    enableVerticalPodAutoScaler,
    customerDetailFetchedTime,
    init: initEdit,
    setForm,
    goConfirmationModal,
    goInputModal,
    goDeleteModal,
    putCustomerAndReload,
    deleteCustomerAndReload,
  } = useEditStore();

  const {
    finalUpdateTime,
    init: initContractList,
    fetchContracts,
  } = useContractListStore();

  const { clearSelectedCustomer } = useListStore();

  const { formState, register, getValues, reset, watch } =
    useForm<CustomerFormFields>({
      defaultValues: initCustomerFormFields,
      mode: 'all',
    });

  // 契約種別のフォームの値
  const watchChannel = watch('channel');
  const watchStatus = watch('status');
  const watchEnableVerticalPodAutoScaler = watch('enableVerticalPodAutoScaler');

  useEffect(() => {
    reset({
      name,
      detail,
      environment,
      environmentType,
      corporateNumber,
      channel,
      poseidonUrl,
      status,
      systemUpdateGroup,
      iapClientId,
      iapClientSecret,
      currentSystemVersion,
      targetSystemVersion,
      poseidonAgentCpu,
      poseidonAgentMemory,
      enableVerticalPodAutoScaler,
    });
  }, [
    channel,
    corporateNumber,
    detail,
    environment,
    environmentType,
    name,
    poseidonUrl,
    status,
    systemUpdateGroup,
    iapClientId,
    iapClientSecret,
    currentSystemVersion,
    targetSystemVersion,
    poseidonAgentCpu,
    poseidonAgentMemory,
    enableVerticalPodAutoScaler,
    customerDetailFetchedTime,
    reset,
  ]);

  useEffect(() => {
    void fetchContracts();
  }, [fetchContracts, finalUpdateTime]);

  /**
   * モーダルを閉じたときにおこなう一連の初期化処理
   */
  const initState = () => {
    // フォームをリセット
    reset();
    // 編集画面の state を初期化
    initEdit();
    // 契約一覧の state を初期化
    initContractList();
    // 選択された顧客をクリア
    clearSelectedCustomer();
  };

  /**
   * すべてのモーダルで右上の「×」ボタンをクリックしたときの処理
   */
  const handleClose = () => {
    // 初期化
    initState();
  };

  /**
   * 入力モーダルで「確認」ボタンをクリックしたときの処理
   */
  const handleClickGoConfirmation = () => {
    // フォームの内容を state に保存
    setForm(getValues());
    // 確認モーダルに進む
    goConfirmationModal();
  };

  /**
   * 入力モーダルで「削除」ボタンをクリックしたときの処理
   */
  const handleClickGoDelete = () => {
    // フォームの内容を state に保存
    setForm(getValues());
    // 削除モーダルに遷移する
    goDeleteModal();
  };

  /**
   * 入力モーダル、削除モーダルで「入力画面に戻る」ボタンをクリックしたときの処理
   */
  const handleClickGoInput = () => {
    // 入力モーダルに戻る
    goInputModal();
  };

  /**
   * 確認モーダルで「更新ボタン」をクリックしたときの処理
   */
  const handleClickUpdate = () => {
    // 顧客更新処理＆一覧をリロード
    void putCustomerAndReload();
  };

  /**
   * 削除モーダルで「削除ボタン」をクリックしたときの処理
   */
  const handleClickDelete = () => {
    // 顧客削除処理＆一覧をリロード
    void deleteCustomerAndReload();
  };

  return (
    <>
      {/* 入力モーダルコンテナ */}
      <InputModal
        watchChannel={watchChannel}
        watchStatus={watchStatus}
        watchEnableVerticalPodAutoScaler={watchEnableVerticalPodAutoScaler}
        formState={formState}
        register={register}
        handleClose={handleClose}
        handleClickGoDelete={handleClickGoDelete}
        handleClickGoConfirmation={handleClickGoConfirmation}
      />
      {/* 確認モーダルコンテナ */}
      <ConfirmationModal
        handleClose={handleClose}
        handleClickGoInput={handleClickGoInput}
        handleClickUpdate={handleClickUpdate}
      />
      {/* 削除モーダルコンテナ */}
      <DeleteModal
        handleClose={handleClose}
        handleClickGoInput={handleClickGoInput}
        handleClickDelete={handleClickDelete}
      />
    </>
  );
};
